import { memo, useMemo } from 'react';
import { ToggleChip } from '../ui';
import { fmtClock } from '../../seriesExtractors';
import { legendOf, timelineGaps } from './eventTypes';

/**
 * The type legend: one chip per event type with its colour and count (the most frequent types, then
 * "Other"). Turning a type off hides its events on the timeline, the charts and the replay scrubber alike.
 * onToggle receives the chip's keys: one type's, or every type "Other" holds.
 */
export function EventTypeChips({ types, hidden, onToggle, theme }) {
    const chips = useMemo(() => legendOf(types, undefined, theme), [types, theme]);
    return (
        <div role="group" aria-label="Event types" className="eg-chip-group sd-evtypes">
            {chips.map((ty) => (
                <ToggleChip key={'t:' + ty.key} on={!ty.keys.every((k) => hidden.has(k))} onToggle={() => onToggle(ty.keys)}>
                    <span className="sd-evtypes__chip">
                        <span className="sd-swatch" style={{ background: ty.color }} aria-hidden="true" />
                        <span className="sd-evtypes__label">{ty.label}</span>
                        <span className="sd-evtypes__n">{ty.count}</span>
                    </span>
                </ToggleChip>
            ))}
        </div>
    );
}

// One event: re-rendered only when it becomes, or stops being, the event at the playhead.
const EventRow = memo(function EventRow({ e, gap, first, isNow, onSeek }) {
    return (
        <li className="sd-tl__item">
            {!first ? (
                <div className={'sd-tl__gap' + (gap.long ? ' sd-tl__gap--break' : '')} style={{ height: gap.px }} aria-hidden="true">
                    {gap.label ? <span className="sd-tl__gaplabel">{gap.label}</span> : null}
                </div>
            ) : null}
            <button
                type="button"
                className={'sd-tl__ev' + (isNow ? ' sd-tl__ev--now' : '')}
                onClick={onSeek ? () => onSeek(e.t) : undefined}
                aria-current={isNow ? 'true' : undefined}
            >
                <span className="sd-tl__time">{fmtClock(e.t)}</span>
                <span className="sd-tl__rail" aria-hidden="true"><span className="sd-tl__dot" style={{ background: e.color }} /></span>
                <span className="sd-tl__text">{e.label}</span>
                <span className="sd-tl__type">{e.typeLabel}</span>
            </button>
        </li>
    );
});

/** The index of the last event at or before t in a list sorted by t; -1 when there is none. */
export function lastAt(events, t) {
    if (t == null) return -1;
    let lo = 0;
    let hi = events.length - 1;
    let found = -1;
    while (lo <= hi) {
        const mid = (lo + hi) >> 1;
        if (events[mid].t <= t + 1e-6) { found = mid; lo = mid + 1; } else hi = mid - 1;
    }
    return found;
}

/**
 * The session's events on a vertical line, top to bottom: each at its time, a dot in its type's
 * colour, its words and its type. The space between two events grows with the time between them
 * (PX_PER_SECOND, up to LONG_GAP seconds; longer gaps are a dashed break), and the gap is written
 * where it fits. Clicking an event plays the replay from there; the last event at the playhead is marked.
 *
 * events: [{ id, t, label, typeLabel, color }] already filtered and coloured (eventTypes.withTypeColors).
 */
export default function EventTimeline({ events, now = null, onSeek, emptyText = 'No events recorded' }) {
    const gaps = useMemo(() => timelineGaps(events), [events]);
    if (!events.length) return <div className="eg-empty"><span className="eg-empty__body">{emptyText}</span></div>;
    const current = lastAt(events, now);
    return (
        <ol className="sd-tl" aria-label="Events">
            {events.map((e, i) => (
                <EventRow key={e.id ?? i} e={e} gap={gaps[i]} first={i === 0} isNow={i === current} onSeek={onSeek} />
            ))}
        </ol>
    );
}
